<script setup>
import { onMounted, onBeforeUnmount, ref, watch } from 'vue'
import Chart from 'chart.js/auto'

const props = defineProps({
  // [{ label, points: [{x, y}], color, gradient?: { stops: [{t, rgb}] } }]
  series: { type: Array, required: true },
  xMin: { type: Number, required: true },
  xMax: { type: Number, required: true },
  sparkline: { type: Boolean, default: false },
  unit: { type: String, default: '' },
  // timestamp of the persistently-selected point (scrub state): drawn on the
  // canvas every frame, so it survives the cursor leaving the chart
  marker: { type: Number, default: null },
})

// Fires while hovering data points on full charts (time scrubbing). The last
// hovered date persists — nothing is emitted on mouseleave.
const emit = defineEmits(['hover-date'])

const canvasRef = ref(null)
let chart = null
let lastHovered = null

const INK_MUTED = '#898781'
const GRID = '#e8e7e1'

const DAY = 86_400_000

const fmtTick = (ms) => {
  const span = props.xMax - props.xMin
  const d = new Date(ms)
  if (span > 3 * 365 * DAY) return String(d.getFullYear())
  if (span > 200 * DAY) return d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

const GRADIENT_ALPHA = 0.85

const gradientFill = (gradient, fallback) => (ctx) => {
  const area = ctx.chart.chartArea
  if (!area) return `${fallback}1a` // first layout pass — chartArea not ready
  const g = ctx.chart.ctx.createLinearGradient(0, area.top, 0, area.bottom)
  // canvas position 0 = top of chart = HIGH values → stop at 1 - t
  for (const s of gradient.stops) {
    g.addColorStop(1 - s.t, `rgba(${s.rgb.join(',')},${GRADIENT_ALPHA})`)
  }
  return g
}

// Break the line where the series has a hole: consecutive points much farther
// apart than the series' own cadence get a NaN marker between them, so nothing
// is interpolated across missing periods.
const withGapBreaks = (points) => {
  if (points.length < 3) return points
  const gaps = points
    .slice(1)
    .map((p, i) => p.x - points[i].x)
    .sort((a, b) => a - b)
  const limit = Math.max(gaps[Math.floor(gaps.length / 2)] * 4, 3 * DAY)
  const out = []
  for (let i = 0; i < points.length; i++) {
    if (i > 0 && points[i].x - points[i - 1].x > limit) {
      out.push({ x: (points[i].x + points[i - 1].x) / 2, y: NaN })
    }
    out.push(points[i])
  }
  return out
}

// The rendered arrays include NaN gap markers, so element indices from hover
// events must be resolved against these, not against props.series[].points.
let builtData = []

const chartData = () => {
  builtData = props.series.map((s) => withGapBreaks(s.points))
  // A single sparkline reads better filled; overlaid series would smear into
  // each other, so multi-series sparklines draw as clean lines only.
  const fillSpark = props.sparkline && props.series.length === 1
  const datasets = props.series.map((s, i) => ({
    label: s.label,
    data: builtData[i],
    spanGaps: false,
    borderColor: s.color,
    backgroundColor: s.gradient
      ? gradientFill(s.gradient, s.color)
      : fillSpark
        ? `${s.color}1a`
        : 'transparent',
    fill: fillSpark || !!s.gradient,
    borderWidth: props.sparkline ? 1.5 : 2,
    pointRadius: 0,
    pointHoverRadius: props.sparkline ? 0 : 4,
    pointHitRadius: props.sparkline ? 0 : 12,
    pointHoverBackgroundColor: s.color,
    pointHoverBorderColor: '#fff',
    pointHoverBorderWidth: 2,
    tension: 0.3,
  }))
  return { datasets }
}

const xScale = (display) => ({
  type: 'linear',
  display,
  min: props.xMin,
  max: props.xMax,
  grid: { display: false },
  border: { color: GRID },
  ticks: {
    color: INK_MUTED,
    font: { size: 10 },
    maxTicksLimit: 6,
    maxRotation: 0,
    callback: (v) => fmtTick(Number(v)),
  },
})

const chartOptions = () => {
  if (props.sparkline) {
    return {
      responsive: true,
      maintainAspectRatio: false,
      animation: false,
      events: [],
      plugins: { legend: { display: false }, tooltip: { enabled: false } },
      scales: { x: xScale(false), y: { display: false } },
    }
  }
  return {
    responsive: true,
    maintainAspectRatio: false,
    animation: false,
    interaction: { mode: 'nearest', axis: 'x', intersect: false },
    onHover: (_e, els) => {
      const el = els[0]
      const point = el ? builtData[el.datasetIndex]?.[el.index] : undefined
      if (!point || !Number.isFinite(point.y)) return
      // Fires on every mousemove within a point's hit radius, not per point.
      const date = new Date(point.x).toISOString().slice(0, 10)
      if (date === lastHovered) return
      lastHovered = date
      emit('hover-date', date)
    },
    plugins: {
      legend: {
        display: props.series.length > 1,
        labels: {
          usePointStyle: true,
          pointStyle: 'circle',
          boxWidth: 6,
          boxHeight: 6,
          color: '#52514e',
          font: { size: 11 },
        },
      },
      // the canvas-drawn marker chip is the value readout — the default
      // popover would duplicate it
      tooltip: { enabled: false },
    },
    scales: {
      x: xScale(true),
      y: {
        grid: { color: GRID },
        border: { display: false },
        ticks: { color: INK_MUTED, font: { size: 10 }, maxTicksLimit: 5 },
      },
    },
  }
}

// Selected-point marker: drawn directly on the canvas from the scrub state, so
// it persists after the cursor leaves and resets when the card collapses
// (scrub state is cleared by the parent).
const drawMarker = (c) => {
  if (props.sparkline || props.marker == null) return
  const data = builtData[0]
  if (!data?.length) return
  let best = null
  for (const p of data) {
    if (!Number.isFinite(p.y)) continue
    if (!best || Math.abs(p.x - props.marker) < Math.abs(best.x - props.marker)) best = p
  }
  if (!best) return
  const { ctx, chartArea, scales } = c
  const px = scales.x.getPixelForValue(best.x)
  const py = scales.y.getPixelForValue(best.y)
  if (px < chartArea.left - 1 || px > chartArea.right + 1) return
  ctx.save()
  ctx.strokeStyle = 'rgba(40,40,40,0.45)'
  ctx.setLineDash([4, 3])
  ctx.lineWidth = 1
  ctx.beginPath()
  ctx.moveTo(px, chartArea.top)
  ctx.lineTo(px, chartArea.bottom)
  ctx.stroke()
  ctx.setLineDash([])
  ctx.fillStyle = '#ffffff'
  ctx.strokeStyle = props.series[0]?.color ?? '#333'
  ctx.lineWidth = 2
  ctx.beginPath()
  ctx.arc(px, py, 4.5, 0, Math.PI * 2)
  ctx.fill()
  ctx.stroke()
  const dateStr = new Date(best.x).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
  const text = `${dateStr} · ${best.y.toFixed(2)}${props.unit ? ` ${props.unit}` : ''}`
  ctx.font = '11px sans-serif'
  const w = ctx.measureText(text).width + 12
  const h = 18
  const bx = Math.min(Math.max(px - w / 2, chartArea.left), chartArea.right - w)
  const by = chartArea.top + 2
  ctx.fillStyle = 'rgba(30,30,30,0.85)'
  ctx.beginPath()
  ctx.roundRect(bx, by, w, h, 4)
  ctx.fill()
  ctx.fillStyle = '#ffffff'
  ctx.textBaseline = 'middle'
  ctx.fillText(text, bx + 6, by + h / 2)
  ctx.restore()
}

const markerPlugin = {
  id: 'monitoringMarker',
  afterDatasetsDraw: (c) => drawMarker(c),
}

onMounted(() => {
  chart = new Chart(canvasRef.value, {
    type: 'line',
    data: chartData(),
    options: chartOptions(),
    plugins: [markerPlugin],
  })
})

// Options are built once at mount and mutated in place. Reassigning them per
// update rebuilt the onHover closure and both scale objects for every chart on
// every poll; a non-deep watch avoids traversing every point as well.
watch(
  () => props.series,
  () => {
    if (!chart) return
    chart.data = chartData()
    if (!props.sparkline) chart.options.plugins.legend.display = props.series.length > 1
    chart.update('none')
  }
)

watch([() => props.xMin, () => props.xMax], () => {
  if (!chart) return
  chart.options.scales.x.min = props.xMin
  chart.options.scales.x.max = props.xMax
  chart.update('none')
})

watch(
  () => props.marker,
  () => chart?.update('none')
)

onBeforeUnmount(() => {
  chart?.destroy()
  chart = null
})
</script>

<template>
  <div class="relative h-full w-full">
    <canvas ref="canvasRef"></canvas>
  </div>
</template>
